<template>
  <div class="mt-3">
    <div class="container-fluid mt-3">
      <b-row class="mb-3 align-items-center">
        <b-col>
          <h4>Market History</h4>
        </b-col>
      </b-row>

      <b-row class="mb-3 align-items-end">
        <b-col sm="6" md="3" lg="2" class="mb-2">
          <b-form-group label="Search">
            <multiselect
              v-model="selectedStockVin"
              :options="filterStockVinOptions"
              :loading="filterStockVinLoading"
              :multiple="true"
              :internal-search="false"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :custom-label="stockVinLabel"
              track-by="vinNumber"
              placeholder="Stock #, VIN..."
              @search-change="onStockVinSearchChange"
              @input="onStockVinSelect"
            >
              <template #noOptions>
                Type at least {{ stockVinMinSearchChars }} characters
              </template>
              <template #noResult>
                <span v-if="stockVinQueryTooShort">
                  Type at least {{ stockVinMinSearchChars }} characters
                </span>
                <span v-else>No results found</span>
              </template>
            </multiselect>
          </b-form-group>
        </b-col>

        <b-col sm="6" md="3" lg="2" class="mb-2">
          <b-form-group label="Vehicle registered from">
            <b-form-input
              v-model="registerDateFrom"
              type="date"
              :max="registerDateTo"
              @change="applyFilters()"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3" lg="2" class="mb-2">
          <b-form-group label="Vehicle registered to">
            <b-form-input
              v-model="registerDateTo"
              type="date"
              :min="registerDateFrom"
              @change="applyFilters()"
            />
          </b-form-group>
        </b-col>

        <b-col sm="6" md="3" lg="2" class="mb-2">
          <b-form-group label="Market registered from">
            <b-form-input
              v-model="createdAtFrom"
              type="date"
              :max="createdAtTo"
              @change="applyFilters()"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" md="3" lg="2" class="mb-2">
          <b-form-group label="Market registered to">
            <b-form-input
              v-model="createdAtTo"
              type="date"
              :min="createdAtFrom"
              @change="applyFilters()"
            />
          </b-form-group>
        </b-col>

        <b-col sm="6" md="3" lg="2" class="mb-2">
          <b-form-group label="Last location">
            <multiselect
              v-model="selectedLastLocation"
              :options="filterLastLocationOptions"
              :loading="filterLastLocationLoading"
              :multiple="true"
              :internal-search="false"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              placeholder="Location..."
              @search-change="onLastLocationSearchChange"
              @input="onLastLocationSelect"
            >
              <template #noOptions>
                Type at least {{ lastLocationMinSearchChars }} characters
              </template>
              <template #noResult>
                <span v-if="lastLocationQueryTooShort">
                  Type at least {{ lastLocationMinSearchChars }} characters
                </span>
                <span v-else>No results found</span>
              </template>
            </multiselect>
          </b-form-group>
        </b-col>

        <b-col sm="6" md="3" lg="2" class="mb-2 d-flex align-items-center">
          <b-form-checkbox v-model="noStocked" @change="applyFilters()">
            No stocked
          </b-form-checkbox>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col class="d-flex justify-content-end align-items-center">
          <b-button variant="yellow" size="sm" @click="applyFilters()">
            Search
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="ml-2"
            @click="clearFilters()"
          >
            Clear filters
          </b-button>
          <b-button
            variant="success"
            size="sm"
            class="ml-2"
            :disabled="marketChangesExcelStatus == 'fetching'"
            @click="downloadExcel()"
          >
            Download excel
            <b-icon icon="file-spreadsheet" />
          </b-button>
          <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            class="ml-2 rows-per-page-select"
            @change="applyFilters()"
          />
        </b-col>
      </b-row>

      <div class="table-responsive">
        <b-table
          show-empty
          bordered
          small
          stacked="md"
          head-variant="light"
          :items="marketChanges.data || []"
          :fields="fields"
          :busy="marketChangesStatus == 'fetching'"
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" variant="yellow" />
            </div>
          </template>
          <template #cell(logo)="{ item }">
            <div class="d-flex align-items-center">
              <img
                v-if="item.logo"
                :src="item.logo"
                :alt="item.name"
                height="24"
                class="mr-2"
              />
              <span>{{ item.name }}</span>
            </div>
          </template>
          <template #cell(vehicle)="{ item }">
            {{ item.year }} {{ item.make }} {{ item.model }}
          </template>
          <template #cell(status)="{ item }">
            <span
              class="status-badge"
              :class="item.status == 1 ? 'status-badge-active' : 'status-badge-old'"
            >
              {{ item.status == 1 ? "Active" : "Old" }}
            </span>
          </template>
        </b-table>
      </div>

      <b-pagination
        v-if="
          marketChangesStatus != 'fetching' &&
          marketChanges &&
          marketChanges.data
        "
        :value="marketChanges ? marketChanges.currentPage : 1"
        :total-rows="marketChanges ? marketChanges.total : 0"
        :per-page="marketChanges ? marketChanges.perPage : 25"
        class="mb-2"
        align="end"
        @change="changePage($event)"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import debounce from "lodash/debounce";
import Multiselect from "vue-multiselect";
import {
  GET_MARKET_CHANGES,
  GET_MARKET_CHANGES_EXCEL,
  GET_FILTER_STOCK_VIN,
  GET_FILTER_LAST_LOCATION,
} from "./actions.js";

export default {
  name: "MarketplaceLogTable",
  components: { Multiselect },
  computed: {
    ...mapState({
      marketChanges: (state) =>
        state.superAdminStore.marketplaceLog.marketChanges.data,
      marketChangesStatus: (state) =>
        state.superAdminStore.marketplaceLog.marketChanges.status,
      marketChangesExcelStatus: (state) =>
        state.superAdminStore.marketplaceLog.marketChangesExcel.status,
      filterStockVinOptions: (state) =>
        state.superAdminStore.marketplaceLog.filterStockVin.data || [],
      filterStockVinLoading: (state) =>
        state.superAdminStore.marketplaceLog.filterStockVin.status ===
        "fetching",
      filterLastLocationOptions: (state) =>
        state.superAdminStore.marketplaceLog.filterLastLocation.data || [],
      filterLastLocationLoading: (state) =>
        state.superAdminStore.marketplaceLog.filterLastLocation.status ===
        "fetching",
    }),
  },
  data() {
    return {
      search: null,
      registerDateFrom: null,
      registerDateTo: null,
      createdAtFrom: null,
      createdAtTo: null,
      lastLocation: null,
      noStocked: false,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      stockVinMinSearchChars: 4,
      lastLocationMinSearchChars: 3,
      selectedStockVin: [],
      stockVinQueryTooShort: true,
      selectedLastLocation: [],
      lastLocationQueryTooShort: true,
      fields: [
        { key: "logo", label: "Marketplace" },
        { key: "stockNumber", label: "Stock #" },
        { key: "vinNumber", label: "VIN" },
        { key: "vehicle", label: "Vehicle" },
        { key: "lastLocation", label: "Last Location" },
        { key: "status", label: "Status", class: "text-center" },
        {
          key: "vehicleRegisterDate",
          label: "Vehicle Registered",
          formatter: (date) => this.$options.filters.formatDateTime(date),
        },
        {
          key: "createdAt",
          label: "Market Registered",
          formatter: (date) => this.$options.filters.formatDateTime(date),
        },
      ],
    };
  },
  created() {
    this.debouncedSearchStockVin = debounce(this.searchStockVin, 300);
    this.debouncedSearchLastLocation = debounce(this.searchLastLocation, 300);
  },
  mounted() {
    this.getData();
  },
  methods: {
    stockVinLabel(option) {
      if (!option) return "";
      return option.vinNumber
        ? `${option.stockNumber} - ${option.vinNumber}`
        : option.stockNumber;
    },
    onStockVinSearchChange(query) {
      this.stockVinQueryTooShort =
        !query || query.trim().length < this.stockVinMinSearchChars;
      if (this.stockVinQueryTooShort) return;
      this.debouncedSearchStockVin(query.trim());
    },
    searchStockVin(query) {
      this.$store
        .dispatch(GET_FILTER_STOCK_VIN, { search: query })
        .catch(() => {});
    },
    onStockVinSelect(options) {
      this.search =
        options && options.length
          ? options.map((option) => option.stockNumber).join(",")
          : null;
      this.applyFilters();
    },
    onLastLocationSearchChange(query) {
      this.lastLocationQueryTooShort =
        !query || query.trim().length < this.lastLocationMinSearchChars;
      if (this.lastLocationQueryTooShort) return;
      this.debouncedSearchLastLocation(query.trim());
    },
    searchLastLocation(query) {
      this.$store
        .dispatch(GET_FILTER_LAST_LOCATION, { search: query })
        .catch(() => {});
    },
    onLastLocationSelect(options) {
      this.lastLocation =
        options && options.length ? options.join(",") : null;
      this.applyFilters();
    },
    filterParams() {
      return {
        search: this.search,
        created_at_from: this.createdAtFrom,
        created_at_to: this.createdAtTo,
        register_date_from: this.registerDateFrom,
        register_date_to: this.registerDateTo,
        lastLocation: this.lastLocation,
        noStocked: this.noStocked ? 1 : 0,
      };
    },
    getData(page = 1) {
      this.$store
        .dispatch(GET_MARKET_CHANGES, {
          ...this.filterParams(),
          page,
          resourcesPerPage: this.perPage,
        })
        .then(() => {})
        .catch(() => {});
    },
    changePage(page) {
      this.getData(page);
    },
    applyFilters() {
      this.getData(1);
    },
    clearFilters() {
      this.search = null;
      this.registerDateFrom = null;
      this.registerDateTo = null;
      this.createdAtFrom = null;
      this.createdAtTo = null;
      this.lastLocation = null;
      this.noStocked = false;
      this.perPage = 25;
      this.selectedStockVin = [];
      this.stockVinQueryTooShort = true;
      this.selectedLastLocation = [];
      this.lastLocationQueryTooShort = true;
      this.getData(1);
    },
    downloadExcel() {
      this.$store
        .dispatch(GET_MARKET_CHANGES_EXCEL, this.filterParams())
        .then((response) => {
          const blob = new Blob([response], {
            type: "application/vnd.ms-excel",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = this.formatFileName("market_history") + ".xls";
          link.click();
        })
        .catch(() => {});
    },
    formatFileName(type) {
      const date = new Date();
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yy = String(date.getFullYear()).slice(-2);

      return `${type}_${dd}-${mm}-${yy}`;
    },
  },
};
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 0.35em 0.65em;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  color: #fff !important;
}
.status-badge-active {
  background-color: #2dce89 !important;
}
.status-badge-old {
  background-color: #8898aa !important;
}

.rows-per-page-select {
  width: auto;
  max-width: 80px;
}

::v-deep .multiselect__tags,
::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder,
::v-deep .multiselect__option,
::v-deep .multiselect__tag {
  font-size: 0.8rem;
}

::v-deep .multiselect__option {
  white-space: normal;
  word-break: break-word;
}

::v-deep .multiselect__content-wrapper {
  overflow-x: hidden;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
